import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  IconButton,
  Typography,
  Slider,
  Tooltip,
  LinearProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Speed as SpeedIcon,
  VolumeUp as VolumeIcon,
} from '@mui/icons-material';
import useTextToSpeech from '../../../hooks/useTextToSpeech';

/**
 * TextToSpeechPlayer - Componente para leitura de texto em voz
 * Usa Web Speech API (gratuito, nativo do navegador)
 */
const TextToSpeechPlayer = ({
  text,
  title,
  compact = false,
  showVoiceSelector = false,
  sx = {},
}) => {
  const {
    isSupported,
    isSpeaking,
    isPaused,
    voices,
    selectedVoice,
    rate,
    progress,
    toggle,
    stop,
    changeRate,
    changeVoice,
  } = useTextToSpeech();

  // Preparar texto completo para leitura
  const fullText = title ? `${title}. ${text}` : text;

  // Vozes em português
  const portugueseVoices = voices.filter(
    (v) => v.lang.startsWith('pt') || v.lang === 'pt-BR' || v.lang === 'pt-PT'
  );

  if (!isSupported) {
    return null; // Não mostrar se não suportado
  }

  // Versão compacta (apenas botão play)
  if (compact) {
    return (
      <Tooltip title={isSpeaking ? (isPaused ? 'Continuar' : 'Pausar') : 'Ouvir artigo'}>
        <IconButton
          onClick={() => toggle(fullText)}
          size="small"
          sx={{
            color: isSpeaking ? 'primary.main' : 'text.secondary',
            ...sx,
          }}
        >
          {isSpeaking && !isPaused ? <PauseIcon /> : <PlayIcon />}
        </IconButton>
      </Tooltip>
    );
  }

  // Versão completa com controles
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 0,
        bgcolor: 'transparent',
        ...sx,
      }}
    >
      {/* Main Controls Row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Play/Pause Button */}
        <Tooltip title={isSpeaking ? (isPaused ? 'Continuar' : 'Pausar') : 'Ouvir artigo'}>
          <IconButton
            onClick={() => toggle(fullText)}
            size="small"
            sx={{
              color: 'primary.main',
              border: '1px solid',
              borderColor: 'divider',
              width: 38,
              height: 38,
              bgcolor: 'action.hover',
              '&:hover': {
                bgcolor: 'action.selected',
                borderColor: 'primary.main'
              },
            }}
          >
            {isSpeaking && !isPaused ? <PauseIcon sx={{ fontSize: 20 }} /> : <PlayIcon sx={{ fontSize: 20 }} />}
          </IconButton>
        </Tooltip>

        {/* Info & Speed control group */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          gap: 0.5
        }}>
          {/* Header/Status line */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: -0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <VolumeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" fontWeight={500} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.65rem' }}>
                Ouvir Artigo
              </Typography>
            </Box>

            {isSpeaking && (
              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: isPaused ? 'warning.main' : 'success.main', fontWeight: 600 }}>
                {isPaused ? 'PAUSADO' : 'REPRODUZINDO'}
              </Typography>
            )}
          </Box>

          {/* Slider Row */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}>
            <Slider
              value={rate}
              min={0.5}
              max={2}
              step={0.25}
              onChange={(_, value) => changeRate(value)}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${v}x`}
              sx={{
                flex: 1,
                padding: '10px 0',
                '& .MuiSlider-thumb': {
                  width: 12,
                  height: 12,
                },
                '& .MuiSlider-track': {
                  height: 3,
                },
                '& .MuiSlider-rail': {
                  height: 3,
                  opacity: 0.2
                }
              }}
              size="small"
            />

            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem', minWidth: 24 }}>
              {rate}x
            </Typography>

            {isSpeaking && (
              <Tooltip title="Parar">
                <IconButton
                  onClick={stop}
                  size="small"
                  sx={{
                    color: 'text.secondary',
                    p: 0.5,
                    '&:hover': { color: 'error.main' }
                  }}
                >
                  <StopIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>

      {/* Progress bar line (only when speaking) */}
      {isSpeaking && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            borderRadius: 1,
            height: 2,
            bgcolor: 'action.hover',
            '& .MuiLinearProgress-bar': {
              borderRadius: 1
            }
          }}
        />
      )}

      {/* Voice selector (optional) - separate row */}
      {showVoiceSelector && portugueseVoices.length > 1 && (
        <FormControl size="small" fullWidth>
          <Select
            value={selectedVoice?.name || ''}
            onChange={(e) => {
              const voice = voices.find((v) => v.name === e.target.value);
              if (voice) changeVoice(voice);
            }}
            sx={{ fontSize: '0.75rem' }}
            displayEmpty
          >
            {portugueseVoices.map((voice) => (
              <MenuItem key={voice.name} value={voice.name} sx={{ fontSize: '0.75rem' }}>
                {voice.name.split(' ')[0]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Box>
  );
};

TextToSpeechPlayer.propTypes = {
  text: PropTypes.string.isRequired,
  title: PropTypes.string,
  compact: PropTypes.bool,
  showVoiceSelector: PropTypes.bool,
  sx: PropTypes.object,
};

export default TextToSpeechPlayer;
